import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AuthService } from '@services/auth.service';
import { OrchestrationAccessService } from '@services/orchestration-access.service';

export function isAiModuleEntry(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return path === '/ai' || path.startsWith('/ai/');
}

/** The access-management entry is independent of other orchestration destinations. */
export function isAccessAdministrationEntry(url: string): boolean {
  const path = url.split('?')[0].split('#')[0].replace(/\/$/, '');
  return path === '/admin/process-management/access' || path === '/admin/orchestration/access';
}

/** Paths that only enter the orchestration shell, including legacy redirects into it. */
export function isOrchestrationAdminEntry(url: string): boolean {
  const path = url.split('?')[0].split('#')[0];
  return path.startsWith('/admin/process-management')
    || path.startsWith('/admin/orchestration')
    || path.startsWith('/admin/developer-catalog')
    || path === '/admin/external-service-calls'
    || path === '/admin/notifications'
    || path.startsWith('/admin/notifications/')
    || isAiModuleEntry(path);
}

@Injectable({
  providedIn: 'root'
})
export class AdminGuard implements CanActivate, CanActivateChild {
  constructor(
    private authService: AuthService,
    private router: Router,
    private orchestrationAccess: OrchestrationAccessService,
  ) {}

  /** Rechecks capability changes while navigating between children of an already active admin shell. */
  canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    return this.canActivate(route, state);
  }

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (!this.authService.isAuthenticated()) {
      return this.router.createUrlTree(['/main-menu']);
    }

    if (this.authService.isAdmin()) {
      return true;
    }

    if (isAccessAdministrationEntry(state.url)) {
      return this.orchestrationAccess.hasAccessManage().pipe(
        map(allowed => allowed ? true : this.router.createUrlTree(['/main-menu'])),
      );
    }

    if (!isOrchestrationAdminEntry(state.url)) {
      return this.router.createUrlTree(['/main-menu']);
    }

    return this.orchestrationAccess.hasOrchestrationAdmin().pipe(
      map(allowed => allowed ? true : this.router.createUrlTree(['/main-menu'])),
    );
  }
}
